<template>
  <div class="container mx-auto px-5 md:px-0 pb-8">
    <Breadcrumb class="hidden md:flex mt-6" :items="items" />
    <section class="faqs-container">
      <!-- Title -->
      <SectionTitle class="mt-6 lg:mt-10 mb-2 faq__title font-body"> -{{ $t('title') }} </SectionTitle>

      <h1 class="lg:text-2.5xl text-xl-1 uppercase mb-6">
        {{ $t('frequentlyAsked') }}
      </h1>

      <!-- Content -->
      <div class="flex flex-col">
        <FaqsFilters
          class="mt-2"
          :categories="categories"
          :filter-by="selectedTopicId"
          :fetching="isFetchingCategories"
          @filter="setSelectedFilter"
        />

        <Spinner v-if="isFetchingFaqs" class="mt-6 w-6 h-6 mx-auto" />
        <template v-else>
          <FaqsList v-if="!isAllTopicsSelected" :faqs-list="categoryQuestions" />
          <FaqsList v-else :faqs-list="allQuestions" />
        </template>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import type { FaqCategory } from '@robustastudio/e-commerce/common';
import type { Faq } from '@robustastudio/e-commerce/common';

const { t: $t } = useI18n({
  useScope: 'local',
});

const { t } = useI18n({ useScope: 'local' });

const { fetchFilteredFAQs, isFetching: isFetchingFilteredFAQs } = useFilteredFAQs();
const selectedTopicId = ref(0);
const categoryQuestions = ref<Faq[]>([]);
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const selectedQuestionId = ref('');
const isFetchingFaqs = ref(true);

const faqsNotFound = ref(false);
const isAllTopicsSelected = computed(() => {
  return selectedTopicId.value === 0;
});

const { data: categories, pending: isFetchingCategories } = useAsyncData(async () => {
  const categories = await useFAQsCategories();
  return categories;
});

const { data: allQuestions, pending: isFetchingInitialFAQs } = useAsyncData(async () => {
  const allQuestions = await useAllFAQs();
  return allQuestions.value;
});

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const faqsList = ref(allQuestions);
/**
 * Watch the overall loading status for both all and filtered FAQs.
 */
watch(
  [isFetchingFilteredFAQs, isFetchingInitialFAQs],
  () => {
    isFetchingFaqs.value = isFetchingInitialFAQs.value || isFetchingFilteredFAQs.value;
  },
  { immediate: true },
);

const { redirect } = useAppRouter();

watch([allQuestions, categoryQuestions], () => {
  if (!isFetchingFaqs.value) {
    if (!isAllTopicsSelected.value) {
      faqsNotFound.value = !(categoryQuestions.value && categoryQuestions.value.length);
    } else {
      faqsNotFound.value = !(allQuestions.value && allQuestions.value.length);
    }
  }
});

/**
 * on changing selected category fetch its filtered questions.
 */
watch(selectedTopicId, async newValue => {
  const questions = await fetchFilteredFAQs(newValue);
  categoryQuestions.value = toRaw(questions as any) || [];
});

useSeoMeta(
  computed(() => ({
    title: t('faq') as string,
    description: t('description') as string,
  })).value,
);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
function displayTopicFAQs(id: number) {
  selectedTopicId.value = id;
}

function setSelectedFilter(category?: FaqCategory) {
  selectedTopicId.value = category?.id || 0;
  const encodedUrl = encodeURIComponent(category?.slug || '');
  redirect(`/faqs/${encodedUrl}`);
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const items = [
  {
    label: $t('home').toString(),
    path: '',
  },
  {
    label: $t('faq').toString(),
    path: 'faqs',
  },
];
</script>

<style lang="postcss" scoped>
.faq__title {
  :deep(.highlight)::before {
    width: 124px;
    height: 44px;
    transform: translateX(-50%) translateY(-2px);

    @screen lg {
      width: 303px;
      height: 110px;
      transform: translateX(-50%) translateY(-6px);
    }
  }
}

.faqs-container {
  @apply mx-auto;
  max-width: 740px;
}
</style>

<i18n>
  {
    "en": {
      "title": "FAQs",
      "description": "Discover frequently asked questions",
      "faq": "FAQs",
      "frequentlyAsked": "FREQUENTLY ASKED",
      "filterBy": "Filter By",
      "home": "Home"
    },
    "ar": {
      "faq": "الأسئلة الشائعة",
      "description": "استكثف لأسئلة الشائعة",
      "title": "الأسئلة الشائعة",
      "frequentlyAsked": "الأسئلة الشائعة",
      "filterBy": "تصفية حسب",
      "home": "الرئيسية"
    }
  }
</i18n>
